/**
 * studentTutorConversationViewModel — pure `(reduxState, input) → props` de la conversation de
 * l'élève avec un tuteur (`<StudentTutorConversationPage>`), un sélecteur par conversation montée
 * (`makeStudentTutorConversationViewModel`).
 *
 * Patron A, avec une boîte d'envoi locale :
 *  - le fil lu vit dans le compte (`currentUser.messages[threadId]`, rempli par `getMessages` et
 *    `getPageMessages`) ; le tuteur et son fil se lisent sur le compte aussi ;
 *  - ce que l'élève envoie vit dans l'écran (`outbox`, de `useStudentTutorConversationState`) : aucun
 *    réducteur ne range la réponse d'un envoi de texte ou de document, et celle d'une photo se range
 *    mal (voir `toUIConversationMessages`). Un message parti garde son id local pour toute la vie de
 *    l'écran ; sa copie lue plus tard dans le fil est écartée (même id serveur).
 * Le reste (l'erreur de lecture, la pagination, la lecture d'un vocal) arrive en `input`, champ par
 * champ : l'écran reconstruit l'objet à chaque rendu, la mémoïsation tient sur ses champs.
 *
 * Le groupement en jours et en salves est celui du fil (`groupConversation`, `ui/formatters`, que
 * le design system lit aussi) ; le view-model rend ses clés STABLES (`stableConversationKeys`).
 */
import { createSelector } from "reselect"

import { groupConversation } from "../../../ui/formatters/conversationFormatter"
import { type ApiSchoolSubject } from "../../../adapters/parent/shared/schoolSubjectAdapter"
import {
    chatActivityStatuses,
    toUIConversationMessage,
    toUIConversationMessages,
    tutorConversationTarget,
    sortBySentAt,
    type ActivityStatusOf,
    type ApiConversationUser,
    type ApiMessengerMessage,
    type MessengerFailure,
} from "../../../adapters/student/Messaging/studentConversationAdapter"
import { toStudentTutors, type ApiStudentTutoringUser } from "../../../adapters/student/Tutoring/studentTutoringAdapter"
import { subjectSummary } from "../../../ui/formatters/liveSessionFormatter"
import { type UITutor } from "../../../ui/types"
import {
    type UIConversationDay,
    type UIConversationMessage,
    type UIMessageAttachment,
} from "../../../ui/types/studentConversation"
import { selectDisplayedStudent } from "../../shared/displayedStudent"

/** Un envoi de l'élève, tenu par l'écran tant qu'il vit. */
export type ConversationOutboxEntry = {
    /** `local-…` : l'id du message dans le fil, du premier rendu à la sortie de l'écran. */
    localId: string
    /** ISO, l'heure du geste. */
    sentAt: string
    text?: string
    /** L'aperçu local d'une pièce (l'adresse choisie), jusqu'à la réponse du serveur. */
    attachment?: UIMessageAttachment
    status: "sending" | "failed" | "sent"
    /** La réponse du serveur, une fois parti. */
    server?: ApiMessengerMessage
    /** Refusé : ce que le serveur en a dit, et si « Renvoyer » a un sens. */
    failure?: MessengerFailure
}

export type StudentTutorConversationInput = {
    /** `null` : le tuteur principal (la route `messenger` sans paramètre). */
    tutorId: string | number | null
    /** La base des chemins de pièces (`API_URL`). */
    apiUrl: string
    outbox: ConversationOutboxEntry[]
    /** La lecture du fil a échoué. */
    error: unknown
    hasEarlier: boolean
    /**
     * Le vocal en cours d'écoute et la durée que son lecteur affiche. Son état de lecture (en
     * cours, avancement) n'entre pas ici : il bouge quatre fois par seconde, et le crochet pose
     * `voicePlayback` sans refaire le fil.
     */
    voiceMessageId: string | null
    voiceLabel: string
}

export type StudentTutorConversationViewModel = {
    tutor: UITutor
    /** Sous son nom : ses matières (celles de ses séances), comme sa ligne de « Messages ». */
    caption?: string
    threadId: string | null
    days: UIConversationDay[]
    /** Le fil à plat, du plus ancien au plus récent : la visionneuse et les gestes y retrouvent un message. */
    messages: UIConversationMessage[]
    loading: boolean
    error: string | null
    hasEarlier: boolean
}

/** Les mots de la notification legacy quand le fil d'un message reçu est introuvable. */
export const CONVERSATION_NOT_FOUND = "La conversation n'a pas été trouvée"
/** Le repli de `<StudentTutorConversationPage>` quand l'erreur n'est pas une phrase. */
const LOAD_ERROR = "Impossible de charger la conversation."

/** Un envoi → son message dans le fil. Parti, il prend la forme que le serveur a rangée, sous son id local. */
function outboxMessage(
    entry: ConversationOutboxEntry,
    selfId: string | number | null | undefined,
    apiUrl: string,
    activityStatusOf: ActivityStatusOf
): UIConversationMessage {
    if (entry.status === "sent" && entry.server != null) {
        const sent = toUIConversationMessage(entry.server, selfId, apiUrl, activityStatusOf)
        if (sent != null) return { ...sent, id: entry.localId, author: "self" }
    }
    const failure = entry.status === "failed" ? entry.failure : undefined
    return {
        id: entry.localId,
        author: "self",
        sentAt: entry.sentAt,
        ...(entry.text ? { text: entry.text } : {}),
        ...(entry.attachment != null ? { attachment: entry.attachment } : {}),
        ...(entry.status !== "sent" ? { status: entry.status } : {}),
        ...(failure?.reason != null ? { failureReason: failure.reason } : {}),
        ...(failure?.retryable === false ? { retryable: false } : {}),
    }
}

/**
 * Le fil complet : ce qui est lu, plus ce qui est parti d'ici (à sa place dans le temps), plus ce
 * qui part ou n'est pas parti, au bout, dans l'ordre des gestes — comme l'ancien fil, qui posait
 * l'envoi en dernier.
 */
function mergeConversation(
    read: UIConversationMessage[],
    outbox: ConversationOutboxEntry[],
    selfId: string | number | null | undefined,
    apiUrl: string,
    activityStatusOf: ActivityStatusOf
): UIConversationMessage[] {
    const sentServerIds = new Set(
        outbox.filter((entry) => entry.server?.id != null).map((entry) => String(entry.server?.id))
    )
    const sent = outbox
        .filter((entry) => entry.status === "sent")
        .map((entry) => outboxMessage(entry, selfId, apiUrl, activityStatusOf))
    const pending = outbox
        .filter((entry) => entry.status !== "sent")
        .map((entry) => outboxMessage(entry, selfId, apiUrl, activityStatusOf))
    const kept = read.filter((message) => !sentServerIds.has(message.id))
    return [...sortBySentAt([...kept, ...sent]), ...pending]
}

/**
 * Des clés qui ne bougent pas quand le fil grandit, par l'un ou l'autre bout.
 *
 * `groupConversation` nomme un jour et une salve par leur PREMIER message : charger des messages
 * plus anciens du même jour changeait ces clés, et remontait tout le jour. Ici :
 *  - un jour daté se nomme par son libellé (« Aujourd'hui », « Lundi 5 octobre ») ;
 *  - la salve la plus ancienne du fil, la seule que les messages plus anciens peuvent allonger par
 *    le haut, se nomme par son DERNIER message ; les autres gardent le premier, qui ne bouge pas
 *    quand un message s'ajoute au bout.
 */
function stableConversationKeys(days: UIConversationDay[]): UIConversationDay[] {
    return days.map((day, dayIndex) => ({
        ...day,
        id: day.label != null ? `day-${day.label}` : day.id,
        bursts: day.bursts.map((burst, burstIndex) => {
            const oldest = dayIndex === 0 && burstIndex === 0
            const anchor = oldest ? burst.messages[burst.messages.length - 1] : burst.messages[0]
            return anchor != null ? { ...burst, id: `burst-${anchor.id}` } : burst
        }),
    }))
}

/** Le lecteur d'un vocal dit ce que sa lecture a mesuré ; les autres vocaux n'ont pas encore de durée. */
function withVoiceLabel(messages: UIConversationMessage[], messageId: string | null, label: string) {
    if (messageId == null || label === "") return messages
    return messages.map((message) =>
        message.id === messageId && message.attachment?.kind === "voice"
            ? { ...message, attachment: { ...message.attachment, durationLabel: label } }
            : message
    )
}

/**
 * Le fil d'une conversation, prêt à poser : ce qui est lu, la boîte d'envoi de l'écran, la durée du
 * vocal qu'on écoute, puis les jours et les salves à clés stables. Le même pour les deux côtés de la
 * messagerie (l'élève et son tuteur, le tuteur et son élève).
 */
export function conversationThread(input: {
    raw: unknown
    outbox: ConversationOutboxEntry[]
    selfId: string | number | null | undefined
    apiUrl: string
    activityStatusOf: ActivityStatusOf
    voiceMessageId: string | null
    voiceLabel: string
}): { messages: UIConversationMessage[]; days: UIConversationDay[] } {
    const { raw, outbox, selfId, apiUrl, activityStatusOf, voiceMessageId, voiceLabel } = input
    const read = toUIConversationMessages(raw, selfId, apiUrl, activityStatusOf)
    const messages = withVoiceLabel(
        mergeConversation(read, outbox, selfId, apiUrl, activityStatusOf),
        voiceMessageId,
        voiceLabel
    )
    return { messages, days: stableConversationKeys(groupConversation(messages)) }
}

/** L'enveloppe d'erreur de l'API n'est pas une phrase pour l'élève : la page dit la sienne. */
export function conversationErrorText(error: unknown): string {
    return typeof error === "string" && error.trim() !== "" ? error : LOAD_ERROR
}

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any
type In = StudentTutorConversationInput

/**
 * Une fabrique, un sélecteur par conversation montée : deux peuvent l'être en même temps (le volet
 * de « Messages » au large, et `messenger` poussé par-dessus), chacune avec sa boîte d'envoi, son
 * tuteur et son lecteur vocal. Le cache de reselect n'a qu'une entrée : partagé, chaque lecture
 * chasserait l'autre et les deux regrouperaient tout le fil à chaque action Redux. Le crochet garde
 * la sienne dans un `useMemo`.
 */
export const makeStudentTutorConversationViewModel = () =>
    createSelector(
        [
            (state: ReduxState) => selectDisplayedStudent(state) as ApiConversationUser | null,
            (state: ReduxState) => state?.data?.staticData?.school_subjects as ApiSchoolSubject[] | undefined,
            (_state: ReduxState, input: In) => input.tutorId,
            (_state: ReduxState, input: In) => input.apiUrl,
            (_state: ReduxState, input: In) => input.outbox,
            (_state: ReduxState, input: In) => input.error,
            (_state: ReduxState, input: In) => input.hasEarlier,
            (_state: ReduxState, input: In) => input.voiceMessageId,
            (_state: ReduxState, input: In) => input.voiceLabel,
        ],
        (
            user,
            subjectsOrNil,
            tutorId,
            apiUrl,
            outbox,
            error,
            hasEarlier,
            voiceMessageId,
            voiceLabel
        ): StudentTutorConversationViewModel => {
            const { tutor, threadId } = tutorConversationTarget(user, tutorId)
            const selfId = user?.id
            const raw = threadId != null ? user?.messages?.[threadId] : undefined
            // La pastille d'une photo devenue activité se lit sur les activités du compte.
            const activityStatusOf = chatActivityStatuses(user)
            const { messages, days } = conversationThread({
                raw,
                outbox,
                selfId,
                apiUrl,
                activityStatusOf,
                voiceMessageId,
                voiceLabel,
            })

            const shownTutor: UITutor = tutor ?? { id: tutorId ?? 0, firstname: "", lastname: "" }
            const row = toStudentTutors(user as ApiStudentTutoringUser | null, subjectsOrNil ?? []).tutors.find(
                (candidate) => String(candidate.tutor.id) === String(shownTutor.id)
            )
            const caption = row?.subjects != null && row.subjects.length > 0 ? subjectSummary(row.subjects) : undefined

            // Une relecture qui échoue sur un fil déjà en cache le laisse lisible : l'erreur ne remplace
            // que le fil qu'on n'a jamais pu lire.
            const failed =
                threadId == null
                    ? CONVERSATION_NOT_FOUND
                    : error != null && raw == null
                      ? conversationErrorText(error)
                      : null

            return {
                tutor: shownTutor,
                ...(caption != null ? { caption } : {}),
                threadId,
                days,
                messages,
                // Le squelette tant que le fil n'a jamais été lu : un fil déjà en cache se montre pendant sa relecture.
                loading: failed == null && raw == null,
                error: failed,
                hasEarlier: failed == null && hasEarlier,
            }
        }
    )
